import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { Base, MagnifyingGlassIcon } from '@itsrever/design-system'
import { DateRangePicker } from '@/ui/shared/date-range'
import { FilterSelect } from '@/ui/shared/checklist'
import { STATUSES } from './withdrawals-columns'

const { Input } = Base

// What "Clear all" clears: everything but the tab.
const FILTER_PARAMS = ['search', 'from', 'to', 'status']

export const WithdrawalsFilters = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const from = searchParams.get('from')
  const to = searchParams.get('to')
  // Read from the URL, so Back and Forward show the search they restore.
  const search = searchParams.get('search') ?? ''

  const resetPage = () => searchParams.delete('page')

  const resetFilters = () => {
    const tab = searchParams.get('return_status')
    navigate(
      tab ? `${location.pathname}?return_status=${tab}` : location.pathname
    )
  }

  return (
    <div className="flex justify-between gap-4 border-y border-neutral-200 pb-3 pt-4">
      <div className="w-[19rem]">
        <Input
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            resetPage()
            if (e.target.value) {
              searchParams.set('search', e.target.value)
            } else {
              searchParams.delete('search')
            }
            setSearchParams(searchParams)
          }}
          type="search"
          placeholder={t('withdrawals.search_by')}
          prefix={<MagnifyingGlassIcon className="size-5" />}
          clear={search.length > 0}
          onClear={() => {
            searchParams.delete('search')
            setSearchParams(searchParams)
          }}
        />
      </div>
      <div className="flex w-fit items-center justify-end gap-2">
        {FILTER_PARAMS.some((param) => searchParams.has(param)) && (
          <Base.Button variant="ghost" onClick={resetFilters}>
            {t('orders.clear_all')}
          </Base.Button>
        )}
        <div className="w-60">
          <DateRangePicker
            selected={{
              from: from ? new Date(from) : undefined,
              to: to ? new Date(to) : undefined
            }}
            onSelect={(date) => {
              resetPage()
              searchParams.set('from', date?.from?.toISOString() ?? '')
              searchParams.set('to', date?.to?.toISOString() ?? '')
              setSearchParams(searchParams)
            }}
          />
        </div>
        <FilterSelect
          param="status"
          label={t('withdrawals.status')}
          options={Object.entries(STATUSES).map(([value, { key }]) => ({
            value,
            label: t(key)
          }))}
          onChange={(values) => {
            resetPage()
            searchParams.delete('status')
            values.forEach((value) => searchParams.append('status', value))
            setSearchParams(searchParams)
          }}
        />
      </div>
    </div>
  )
}
